import { useNavigate } from 'react-router';
import { useAlertNoticesQuery } from '@/graphql/generated';
import { useUserLanguage, useErrorHandlerContext } from '@/customHooks';
import { useDashboardFilter } from '@/contexts/DashboardFilterContext';
import { pathURL } from '@/components/constants/urls';
import textsData from '@/textData/DashboardTexts.json';
import { theme } from '@/styles';
import { formatRelativeDateForAlert } from '@/helpers/formatRelativeDate';
import { formatAlertRentPeriod } from '@/helpers/formatAlertRentPeriod';
import { DashboardCardPeriodCaption, LoadingSpinner } from '../atoms';

const BellIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
      stroke={theme.colors.secondary1}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.73 21a2 2 0 0 1-3.46 0"
      stroke={theme.colors.secondary1}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


const alertNoticesReportPath = `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.ALERT_NOTICES}`;

export const DashboardAlertNoticeCard = () => {
  const navigate = useNavigate();
  const userLanguage = useUserLanguage();
  const { dashboardFilter } = useDashboardFilter();
  const texts =
    userLanguage === 'es' ? textsData.es.alertNoticeCard : textsData.en.alertNoticeCard;
  const { data, loading, error } = useAlertNoticesQuery({
    variables: {
      page: 1,
      pageSize: 10,
      ...(dashboardFilter ? { filter: dashboardFilter } : {}),
    },
    fetchPolicy: 'network-only',
  });
  const { open: errorHandle } = useErrorHandlerContext();

  if (error) errorHandle(error);

  const alerts = data?.alertNotices?.items ?? [];
  const visibleAlerts = alerts.slice(0, 5);

  if (loading) {
    return (
      <div className="box-border flex h-full min-h-[320px] flex-col rounded-xl bg-white px-5 py-4 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex min-w-0 flex-col items-start">
            <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
            <DashboardCardPeriodCaption tightTop />
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <button className="m-0 whitespace-nowrap border-none bg-transparent p-0 text-sm font-bold text-[#4a9462] hover:underline" type="button" onClick={() => navigate(alertNoticesReportPath)}>
              {texts.viewAll}
            </button>
          </div>
        </div>
        <hr className="mb-4 mt-[0.4rem] border-0 border-t border-t-[#E5E7EB]" />
        <div className="flex flex-1 items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="box-border flex h-full min-h-[320px] flex-col rounded-xl bg-white px-5 py-4 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex min-w-0 flex-col items-start">
          <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
          <DashboardCardPeriodCaption tightTop />
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button className="m-0 whitespace-nowrap border-none bg-transparent p-0 text-sm font-bold text-[#4a9462] hover:underline" type="button" onClick={() => navigate(alertNoticesReportPath)}>
            {texts.viewAll}
          </button>
        </div>
      </div>
      <hr className="mb-4 mt-[0.4rem] border-0 border-t border-t-[#E5E7EB]" />
      <div className="flex-1 overflow-y-auto">
        {visibleAlerts.length === 0 ? (
          <p className="m-0 py-4 text-base text-gray-500">{texts.empty}</p>
        ) : (
          visibleAlerts.map((alert) => (
            <div key={alert.id} className="flex items-start gap-3 border-b border-b-[#E5E7EB] py-3 last:border-b-0">
              <div className="flex h-10 w-10 min-w-10 items-center justify-center rounded-lg bg-[#F8FAFC]">
                <BellIcon />
              </div>
              <div className="min-w-0 flex-1">
                <p className="m-0 text-base leading-[1.4] text-[#1F2937]">
                  <strong className="font-bold text-[#0B3465]">{alert.tenantName}</strong>
                  {' '}
                  {texts.messageTemplate}{' '}
                  {formatAlertRentPeriod(alert.period, userLanguage)}
                </p>
                <p className="mb-0 ml-0 mr-0 mt-1 text-[0.8rem] text-gray-500">
                  {formatRelativeDateForAlert(alert.dueDate, alert.createdAt, {
                    overdueDays: texts.overdueDays,
                    overdueDay: texts.overdueDay,
                    dueInDays: texts.dueInDays,
                    dueInDay: texts.dueInDay,
                    justNow: texts.justNow,
                    minutesAgo: texts.minutesAgo,
                    hourAgo: texts.hourAgo,
                    hoursAgo: texts.hoursAgo,
                  })}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
